import React, { useState, useEffect, useRef } from 'react';
import { createMuiTheme } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/styles';
import semver from 'semver';
import './css/App.css';
import { AppContext } from './common/contexts';
import PageBase from './PageBase';
// import PageBasePreLaunch from './PageBasePreLaunch';
import LaunchScreen from './LaunchScreen';
import {
    initFirebaseAuth,
    initFirebaseAuthLocal,
    unsubscribeRealtimeUpdates,
    // getFirebaseMessageToken
} from './system/firebase';
import { TEMP_SWITCH_USER_IDS } from './system/constants';
import 'react-credit-cards/es/styles-compiled.css';
import { GlobalDebug } from './removeConsole';
import { loadLovsUnauthenticated, loadSys } from './common/services/appServices';
import RefreshAppDialog from './common/elemental/RefreshAppDialog';

const defaultMaterialTheme = createMuiTheme({
    palette: {
        primary: { main: '#FC821F', contrastText: '#FFF' },
        secondary: { main: '#1D8A8B', contrastText: '#FFF' },
    },
    /*
      value         |0px     360px    768px    1024px   1440px
      key           |xs      sm       md       lg       xl
      screen width  |--------|--------|--------|--------|-------->
      range         |   xs   |   sm   |   md   |   lg   |   xl
    */
    breakpoints: {
        values: {
            xs: 0,
            sm: 360,
            md: 768,
            lg: 1024,
            xl: 1440,
        },
    },
});

function App() {
    const draftReplies = useRef();
    const [auth, setAuth] = useState(null);
    const [authUser, setAuthUser] = useState(null);
    const [isAuthenticationPending, setIsAuthenticationPending] = useState(true);
    const [sys, setSys] = useState();
    const [lovs, setLovs] = useState();
    // const [isTokenFound, setTokenFound] = useState(false);
    // const [isOkToLoadApp, setIsOkToLoadApp] = useState(false);
    const [refreshAppDialogOpen, setRefreshAppDialogOpen] = useState(false);
    const [authRedirectResultChecked, setAuthRedirectResultChecked] = useState(false);
    const [authRedirectResult, setAuthRedirectResult] = useState();

    const minVersion = sys?.minVersionWeb;
    // const targetVersion = sys?.targetVersionWeb;
    const currentVersion = `${process.env.REACT_APP_MAJOR_VERSION}.${process.env.REACT_APP_MINOR_VERSION}.${process.env.REACT_APP_PATCH_VERSION}`;

    /**
     * @REMOVE_CONSOLES
     * remove the working of console logs
     * remove any accidental use of console logs
     */
    useEffect(() => {
        process.env.NODE_ENV === 'production' && (process.env.REACT_APP_ENV === 'PROD' || process.env.REACT_APP_ENV === 'TEST') && GlobalDebug(false);
    }, []);

    /**
     * When auth state changes, set the 'authUser' and mark 'authenticationPending' false.
     * @param {*} user
     */
    const handleChangeAuthState = user => {
        // console.log('\n\n\n\n handleChangeAuthState user', user);
        setAuthUser(user);
        setIsAuthenticationPending(false);
    };

    /**
     * Temporary get Google auth redirect result
     */
    useEffect(() => {
        const checkAuthResult = async () => {
            const result = await auth.getRedirectResult();
            setAuthRedirectResultChecked(true);
            setAuthRedirectResult(result);
            // console.log('\n\n\n\n getRedirectResult', result);
        };

        if (auth) checkAuthResult();
    }, [auth]);

    /**
     * Initialize Firebase
     */
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            initFirebaseAuth(setAuth);
        } else {
            console.log('calling initFirebaseAuthLocal');
            initFirebaseAuthLocal(setAuth);
        }
        // initFirebaseAuth(setAuth);
        console.log('process.env.NODE_ENV', { processEnv: process.env });
        // console.log('isTokenFound', isTokenFound);
    }, []);

    /**
     * When Firebase Auth is ready, load system data and LOV data, and listen to auth state change.
     */
    useEffect(() => {
        let unsubscribeLoadSys = null;
        let unsubscribeLoadLovs = null;

        // if Firebase is initialized, set onAuthStateChange handler
        if (auth) {
            auth.onAuthStateChanged(handleChangeAuthState);
            // getFirebaseMessageToken(setTokenFound);
            draftReplies.current = {};

            unsubscribeLoadSys = loadSys(setSys);

            // load countryLov and payFrequencyLov
            unsubscribeLoadLovs = loadLovsUnauthenticated(setLovs);
        }

        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeLoadSys, unsubscribeLoadLovs]);
        };
    }, [auth]);

    /**
     * Check version and force reload if lower than minimum version
     */
    useEffect(() => {
        // execute only when the versions are loaded
        if (currentVersion && minVersion) {
            const needUpgrade = semver.lt(currentVersion, minVersion);

            // if upgrade is required, show alert with button that refreshes the app
            if (needUpgrade) {
                // setIsOkToLoadApp(false);
                setRefreshAppDialogOpen(true);
                // setIsVersionMonitoring(false); // reset it so it can be triggered again
                ////
            } else if (!needUpgrade) {
                // NOTE: Do not turn off the version monitoring even upgrade is not required!
                ////
                // // load app only if it is greater than or equal to the minimum required version
                // setIsOkToLoadApp(true);
            }
        }
    }, [currentVersion, minVersion]);

    // temp switch user
    const tempSwitchUser = key => {
        const uid = TEMP_SWITCH_USER_IDS;
        setAuthUser({ uid: uid[key], emailVerified: true });
    };
    // END OF temp switch user

    return (
        <ThemeProvider theme={defaultMaterialTheme}>
            <AppContext.Provider
                value={{
                    auth,
                    authUser,
                    isAuthenticationPending,
                    setIsAuthenticationPending,
                    draftReplies,
                    sys,
                    setSys,
                    lovs,
                    setLovs,
                    authRedirectResult,
                    /*temp switch user*/ tempSwitchUser /*END OF temp switch user*/,
                }}
            >
                <div className="App">
                    {auth && authRedirectResultChecked && <PageBase />}
                    {/* {auth && <PageBasePreLaunch />} */}
                    {!(auth && authRedirectResultChecked) && <LaunchScreen />}
                </div>
                <RefreshAppDialog open={refreshAppDialogOpen} />
            </AppContext.Provider>
        </ThemeProvider>
    );
}

export default App;
